import { TaxProcedure } from '@carvia/ros-client-types';
import { resolveDefaultBookingTaxProcedure } from '@/utils/resolve-default-booking-tax-procedure';
import { resolvePlaceOfSupply, type TaxLegalEntity } from '@/utils/resolve-tax-country';
import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';

type TaxProcedureInputs = {
  startDate: Date | string;
  endDate: Date | string;
  vatId?: string | null;
  country?: string | null;
  handoverCountry?: string | null;
};

function inputsKey(inputs: TaxProcedureInputs): string {
  return JSON.stringify({
    startDate: new Date(inputs.startDate).toISOString(),
    endDate: new Date(inputs.endDate).toISOString(),
    vatId: inputs.vatId ?? null,
    country: inputs.country ?? null,
    handoverCountry: inputs.handoverCountry ?? null,
  });
}

export function useSuggestedTaxProcedure(params: {
  startDate: MaybeRefOrGetter<Date | string>;
  endDate: MaybeRefOrGetter<Date | string>;
  vatId?: MaybeRefOrGetter<string | null | undefined>;
  country: MaybeRefOrGetter<string | null | undefined>;
  handoverCountry?: MaybeRefOrGetter<string | null | undefined>;
  /** Legal entity renting out the car. */
  legalEntity: MaybeRefOrGetter<TaxLegalEntity>;
  initialTaxProcedure?: MaybeRefOrGetter<TaxProcedure | undefined>;
  taxProcedure?: Ref<TaxProcedure>;
  /**
   * In create mode, suggestion tracks customer/rental inputs immediately.
   * In edit mode, the saved value is kept until customer or rental inputs change.
   */
  mode: 'create' | 'edit';
}) {
  const isManuallySet = ref(false);

  const currentInputs = computed(
    (): TaxProcedureInputs => ({
      startDate: toValue(params.startDate),
      endDate: toValue(params.endDate),
      vatId: toValue(params.vatId),
      country: toValue(params.country),
      handoverCountry: toValue(params.handoverCountry),
    }),
  );

  const suggestedTaxProcedure = computed(() => {
    const legalEntity = toValue(params.legalEntity);
    return resolveDefaultBookingTaxProcedure({
      // Only business customers leave REGULAR. With OSS registration they are taxed at the place
      // of supply; without it only the tax procedure looks at the place of supply.
      taxCountry: legalEntity.isOssRegistered
        ? (resolvePlaceOfSupply({
            startDate: currentInputs.value.startDate,
            endDate: currentInputs.value.endDate,
            handoverCountry: currentInputs.value.handoverCountry,
            customerCountry: currentInputs.value.country,
          }) ?? legalEntity.countryCode)
        : legalEntity.countryCode,
      vatId: currentInputs.value.vatId,
      startDate: currentInputs.value.startDate,
      endDate: currentInputs.value.endDate,
      customerCountry: currentInputs.value.country,
      handoverCountry: currentInputs.value.handoverCountry,
      legalEntity,
    });
  });

  const taxProcedure =
    params.taxProcedure ?? ref(toValue(params.initialTaxProcedure) ?? suggestedTaxProcedure.value);

  const baselineInputsKey = ref(inputsKey(currentInputs.value));

  watch(
    suggestedTaxProcedure,
    (suggested) => {
      if (isManuallySet.value) {
        return;
      }
      // In edit mode the saved value stays until the inputs change, not when the legal entity loads.
      if (params.mode === 'edit' && inputsKey(currentInputs.value) === baselineInputsKey.value) {
        return;
      }
      taxProcedure.value = suggested;
    },
    { immediate: params.mode === 'create' },
  );

  watch(currentInputs, (inputs) => {
    const nextKey = inputsKey(inputs);
    if (params.mode === 'edit' && nextKey === baselineInputsKey.value) {
      return;
    }
    if (!isManuallySet.value) {
      taxProcedure.value = suggestedTaxProcedure.value;
    }
    baselineInputsKey.value = nextKey;
  });

  watch(
    () => toValue(params.initialTaxProcedure),
    (initial) => {
      if (initial === undefined || isManuallySet.value) {
        return;
      }
      taxProcedure.value = initial;
      baselineInputsKey.value = inputsKey(currentInputs.value);
    },
  );

  const setTaxProcedure = (value: TaxProcedure, manual = true) => {
    isManuallySet.value = manual;
    taxProcedure.value = value;
  };

  const applySuggestion = () => {
    isManuallySet.value = false;
    taxProcedure.value = suggestedTaxProcedure.value;
  };

  return {
    taxProcedure,
    suggestedTaxProcedure,
    isManuallySet,
    setTaxProcedure,
    applySuggestion,
  };
}
